import React, { useEffect, useState } from 'react';
//
import FormulaInput from 'components/formula-entry/formula';
import MarkerSearchBox from 'components/formula-entry/marker-search-box';
import MarkerSearchResults from 'components/formula-entry/marker-search-results';
import { IFormulaEntryState, IMarker } from 'store/formula-entry/types';
import { IDeal } from 'store/deals/types';
import { ModalContainerDark, OptionModal } from 'components/modal-provider/modal';
import {
  ButtonContainer,
  CloseIcon,
  Label,
  SaveFormulaButton,
  CancelButton,
  TitleContainer,
  Title,
  ContentSection,
  ContentSectionPanel,
  Container,
  Content,
  InputError,
  InputGroup,
} from 'components/formula-entry/modals/_common';
import Input from 'components/input/text';
import { isMatch } from 'store/formula-entry/match';
import {
  validateFormula,
  getUnitLabel,
  triggerDuplicateMarkerPreventionTimer,
} from 'components/formula-entry/modals/helpers';
import { handleError } from 'lib/helpers/error-handler';
import messages from 'constants/messages';
import { capitalizeFirstLetter } from 'lib/helpers/string-manipulators';
import optionErrors from 'lib/validators/option.errors';
import { EProductField, EProductNestedField } from 'store/field-options/types';
import { ELoadingType } from 'store/loading/types';
import MarkerFilters from '../formula-list/marker-filters';
import { TConcatArraysReturnType } from '../types';

interface IFormulaEntryContainerProps {
  cancelFormulaEdit: () => void;
  dealEdit: IDeal;
  formulaEntry: IFormulaEntryState;
  getDealEvaluation: () => void;
  setLoading: (loading: ELoadingType) => void;
  updateProductField: Function;
  allTypesMarkerList: TConcatArraysReturnType[];
  updateMarkerList: Function;
}

interface IFormula {
  descriptionValue: string;
  formulaValue: string;
}

enum EFormula {
  NONE,
  BASE,
  BATNA,
}

interface IBaseBatnaError {
  baseDescription: string;
  baseFormula: string;
  batnaDescription: string;
  batnaFormula: string;
}

const errorsInitialState: IBaseBatnaError = {
  baseDescription: '',
  baseFormula: '',
  batnaDescription: '',
  batnaFormula: '',
};

export default ({
  cancelFormulaEdit,
  dealEdit,
  formulaEntry,
  getDealEvaluation,
  setLoading,
  updateProductField,
  allTypesMarkerList,
  updateMarkerList,
}: IFormulaEntryContainerProps) => {
  const { availableMarkers, productIndex, markerList } = formulaEntry;
  const { products, formulas: dealFormulas } = dealEdit;

  const [isInitialized, setIsInitialized] = useState(false);
  const [errors, setErrors] = useState(errorsInitialState);
  const [base, setBase] = useState<IFormula>({
    descriptionValue: '',
    formulaValue: '',
  });
  const [batna, setBatna] = useState<IFormula>({
    descriptionValue: '',
    formulaValue: '',
  });

  const [activeFormula, setActiveFormula] = useState(EFormula.BASE);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [filteredMarkers, setFilteredMarkers] = useState(availableMarkers);
  const [markerSearchValue, setMarkerSearchValue] = useState('');
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    setFilteredMarkers(markerList);
  }, [markerList]);

  // Inject marker at cursorPosition
  function insertMarker(name: string) {
    // Check for active timer, if found, prevent insertion of duplicate marker
    if (timer) {
      return;
    }

    // Kickoff new timer to handle double-clicking of markers
    triggerDuplicateMarkerPreventionTimer(setTimer);

    // Determine which formula is being edited
    if (activeFormula === EFormula.BASE) {
      const formula = base.formulaValue;
      const formulaValueUpdated = [formula.slice(0, cursorPosition), `[${name}]`, formula.slice(cursorPosition)].join(
        ''
      );

      setBase({
        ...base,
        formulaValue: formulaValueUpdated,
      });
    } else if (activeFormula === EFormula.BATNA) {
      const formula = batna.formulaValue;
      const formulaValueUpdated = [formula.slice(0, cursorPosition), `[${name}]`, formula.slice(cursorPosition)].join(
        ''
      );

      setBatna({
        ...batna,
        formulaValue: formulaValueUpdated,
      });
    }
  }

  // Update formulas in product
  async function save() {
    if (productIndex === null) {
      handleError(messages.errors.default);
      return;
    }

    setLoading(ELoadingType.NONE);

    // Trim input values
    base.descriptionValue = base.descriptionValue.trim();
    base.formulaValue = base.formulaValue.trim();
    batna.descriptionValue = batna.descriptionValue.trim();
    batna.formulaValue = batna.formulaValue.trim();

    const newErrors = { ...errorsInitialState };

    try {
      // Base
      if (!base.descriptionValue) {
        newErrors.baseDescription = optionErrors.requiredField;
      }

      if (base.formulaValue) {
        const payload = {
          name: base.descriptionValue,
          formula: base.formulaValue,
        };
        const baseErrorResponse = await validateFormula(dealFormulas, [payload]);
        const baseError = baseErrorResponse.data.error;
        if (baseError) {
          newErrors.baseFormula = capitalizeFirstLetter(baseError.message);
        }
      } else {
        newErrors.baseFormula = optionErrors.requiredField;
      }

      // BATNA
      if (!batna.descriptionValue) {
        newErrors.batnaDescription = optionErrors.requiredField;
      }

      if (batna.formulaValue) {
        const payload = {
          name: batna.descriptionValue,
          formula: batna.formulaValue,
        };
        const batnaErrorResponse = await validateFormula(dealFormulas, [payload]);
        const batnaError = batnaErrorResponse.data.error;
        if (batnaError) {
          newErrors.batnaFormula = capitalizeFirstLetter(batnaError.message);
        }
      } else {
        newErrors.batnaFormula = optionErrors.requiredField;
      }
    } catch (e) {
      handleError(e);
      setLoading(ELoadingType.NONE);
    }

    // Update deal, re-evaluate, and close modal if no errors exist
    if (
      !newErrors.baseDescription &&
      !newErrors.baseFormula &&
      !newErrors.batnaDescription &&
      !newErrors.batnaFormula
    ) {
      updateProductField(productIndex, EProductField.BASE_PRICE, base.formulaValue, EProductNestedField.FORMULA);
      updateProductField(
        productIndex,
        EProductField.BASE_PRICE,
        base.descriptionValue,
        EProductNestedField.DESCRIPTION
      );
      updateProductField(productIndex, EProductField.MAIN_BATNA, batna.formulaValue, EProductNestedField.FORMULA);
      updateProductField(
        productIndex,
        EProductField.MAIN_BATNA,
        batna.descriptionValue,
        EProductNestedField.DESCRIPTION
      );
      getDealEvaluation();
      cancelFormulaEdit();
    } else {
      setErrors(newErrors);
      setLoading(ELoadingType.NONE);
    }
  }

  // Initialize fields
  useEffect(() => {
    if (!isInitialized && products && productIndex !== null) {
      setIsInitialized(true);
      const { basePrice, mainBatna } = products[productIndex];
      setBase({
        ...base,
        descriptionValue: basePrice ? basePrice.description : '',
        formulaValue: basePrice ? basePrice.formula : '',
      });
      setBatna({
        ...batna,
        descriptionValue: mainBatna ? mainBatna.description : '',
        formulaValue: mainBatna ? mainBatna.formula : '',
      });
    }
  }, [base, batna, isInitialized, productIndex, products]);

  // Manage filtered marker list
  useEffect(() => {
    if (!availableMarkers) {
      handleError(messages.errors.noMarkers);
      return;
    }
    const markers = markerList ?? allTypesMarkerList;
    const filteredMarkersNew = markers.filter((marker: IMarker) => isMatch(markerSearchValue, marker.name));
    setFilteredMarkers(filteredMarkersNew);
  }, [availableMarkers, markerList, markerSearchValue, allTypesMarkerList]);

  // Update marker value and filtered list
  function handleSearchValueChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!availableMarkers) {
      handleError(messages.errors.noMarkers);
      return;
    }
    const val = e.target.value;
    setMarkerSearchValue(val);
  }

  // Handle clear button for marker search input
  function handleSearchValueClear() {
    setMarkerSearchValue('');
    setFilteredMarkers(availableMarkers);
  }

  return (
    <ModalContainerDark>
      <OptionModal>
        <Container>
          <TitleContainer>
            <Title>Base & Shell BATNA Entry</Title>
            <CloseIcon onClick={cancelFormulaEdit} />
          </TitleContainer>

          <Content>
            <ContentSection>
              <ContentSectionPanel>
                <InputGroup>
                  <Label data-valid={!!errors.baseDescription}>BASE DISPLAY LABEL</Label>
                  <Input
                    type="text"
                    value={base.descriptionValue}
                    onChange={e => setBase({ ...base, descriptionValue: e.target.value })}
                    onFocus={() => setActiveFormula(EFormula.BASE)}
                  />
                  <InputError>{errors.baseDescription}</InputError>
                </InputGroup>

                <InputGroup>
                  <Label data-valid={!!errors.baseFormula}>{`BASE FORMULA ${getUnitLabel(
                    products,
                    productIndex
                  ).toUpperCase()}`}</Label>
                  <FormulaInput
                    formulaInputValue={base.formulaValue}
                    isActive={activeFormula === EFormula.BASE}
                    onChange={e => setBase({ ...base, formulaValue: e.target.value })}
                    setActiveFormula={() => setActiveFormula(EFormula.BASE)}
                    setCursorPosition={(p: number) => setCursorPosition(p)}
                    setFormula={(value: string) => setBase({ ...base, formulaValue: value })}
                  />
                  <InputError>{errors.baseFormula}</InputError>
                </InputGroup>

                <InputGroup>
                  <Label data-valid={!!errors.batnaDescription}>SHELL BATNA DISPLAY LABEL</Label>
                  <Input
                    type="text"
                    value={batna.descriptionValue}
                    onChange={e => setBatna({ ...batna, descriptionValue: e.target.value })}
                    onFocus={() => setActiveFormula(EFormula.BATNA)}
                  />
                  <InputError>{errors.batnaDescription}</InputError>
                </InputGroup>

                <InputGroup>
                  <Label data-valid={!!errors.batnaFormula}>{`SHELL BATNA FORMULA ${getUnitLabel(
                    products,
                    productIndex
                  ).toUpperCase()}`}</Label>
                  <FormulaInput
                    formulaInputValue={batna.formulaValue}
                    isActive={activeFormula === EFormula.BATNA}
                    onChange={e => setBatna({ ...batna, formulaValue: e.target.value })}
                    setActiveFormula={() => setActiveFormula(EFormula.BATNA)}
                    setCursorPosition={(p: number) => setCursorPosition(p)}
                    setFormula={(value: string) => setBatna({ ...batna, formulaValue: value })}
                  />
                  <InputError>{errors.batnaFormula}</InputError>
                </InputGroup>
              </ContentSectionPanel>

              <ContentSectionPanel>
                <Label>SEARCH FOR MARKER</Label>
                <MarkerSearchBox
                  value={markerSearchValue}
                  onChange={handleSearchValueChange}
                  onClear={handleSearchValueClear}
                />
                <MarkerFilters allTypesMarkerList={allTypesMarkerList} updateMarkerList={updateMarkerList} />
                <MarkerSearchResults markers={filteredMarkers} onClick={insertMarker} />
              </ContentSectionPanel>
            </ContentSection>
          </Content>

          <ButtonContainer>
            <CancelButton data-testid="tid-cancel-button" onClick={cancelFormulaEdit}>
              Cancel
            </CancelButton>
            <SaveFormulaButton data-testid="tid-apply-formula-button" onClick={save}>
              Apply Formula
            </SaveFormulaButton>
          </ButtonContainer>
        </Container>
      </OptionModal>
    </ModalContainerDark>
  );
};
